import type { WorkingPlanResponse } from "@rnavoyager/contracts";
import type { TaskEvent } from "./types";

type Step = WorkingPlanResponse["steps"][number];
export interface PlanRevisionMarker {
  revision: number;
  planId: string;
  digest: string;
  timestamp: string;
}

export function planRevisionMarkers(
  events: readonly TaskEvent[],
  turnId: string | null,
): PlanRevisionMarker[] {
  const revisions = new Map<number, PlanRevisionMarker>();
  for (const event of events) {
    if (!turnId || event.turn_id !== turnId || !event.plan_revision) continue;
    if (!revisions.has(event.plan_revision.revision)) {
      revisions.set(event.plan_revision.revision, {
        ...event.plan_revision,
        timestamp: event.timestamp,
      });
    }
  }
  return [...revisions.values()].sort((left, right) => right.revision - left.revision);
}

export interface PlanStepChange {
  step: Step;
  index: number;
}

export interface PlanDifference {
  added: PlanStepChange[];
  removed: PlanStepChange[];
  adjusted: Array<{
    before: Step;
    after: Step;
    previousIndex: number;
    index: number;
    order: boolean;
    acceptance: boolean;
    dependencies: boolean;
  }>;
  progress: Array<{ title: string; before: Step["status"]; after: Step["status"] }>;
}

/** 步骤 ID 在修订时重新生成；按标题对齐，避免把整份规划误报为全部删改。 */
export function comparePlans(
  before: WorkingPlanResponse,
  after: WorkingPlanResponse,
): PlanDifference {
  const previous = before.steps;
  const next = after.steps;
  const width = next.length + 1;
  const lcs = new Uint16Array((previous.length + 1) * width);
  for (let left = previous.length - 1; left >= 0; left -= 1) {
    for (let right = next.length - 1; right >= 0; right -= 1) {
      lcs[left * width + right] =
        previous[left].title === next[right].title
          ? 1 + lcs[(left + 1) * width + right + 1]
          : Math.max(lcs[(left + 1) * width + right], lcs[left * width + right + 1]);
    }
  }
  const pairs = new Map<number, number>();
  const stable = new Set<number>();
  const used = new Set<number>();
  let left = 0;
  let right = 0;
  while (left < previous.length && right < next.length) {
    if (previous[left].title === next[right].title) {
      pairs.set(left, right);
      stable.add(left);
      used.add(right);
      left += 1;
      right += 1;
    } else if (lcs[(left + 1) * width + right] >= lcs[left * width + right + 1]) {
      left += 1;
    } else {
      right += 1;
    }
  }
  // 对齐剩余同名步骤，单独识别移动；插入导致的序号变化不算移动。
  previous.forEach((step, index) => {
    if (pairs.has(index)) return;
    const matched = next.findIndex(
      (candidate, candidateIndex) => !used.has(candidateIndex) && candidate.title === step.title,
    );
    if (matched >= 0) {
      pairs.set(index, matched);
      used.add(matched);
    }
  });
  const difference: PlanDifference = { added: [], removed: [], adjusted: [], progress: [] };
  previous.forEach((step, index) => {
    const matched = pairs.get(index);
    if (matched === undefined) {
      difference.removed.push({ step, index });
      return;
    }
    const following = next[matched];
    const order = !stable.has(index);
    const acceptance = (step.acceptanceCriteria ?? "") !== (following.acceptanceCriteria ?? "");
    const dependencies =
      JSON.stringify(dependencyTitles(step, before).sort()) !==
      JSON.stringify(dependencyTitles(following, after).sort());
    if (order || acceptance || dependencies) {
      difference.adjusted.push({
        before: step,
        after: following,
        previousIndex: index,
        index: matched,
        order,
        acceptance,
        dependencies,
      });
    }
    if (step.status !== following.status) {
      difference.progress.push({
        title: following.title,
        before: step.status,
        after: following.status,
      });
    }
  });
  next.forEach((step, index) => {
    if (!used.has(index)) difference.added.push({ step, index });
  });
  return difference;
}

export function dependencyTitles(step: Step, plan: WorkingPlanResponse): string[] {
  return step.dependsOn.map(
    (id) => plan.steps.find((candidate) => candidate.stepId === id)?.title ?? "未记录的依赖步骤",
  );
}
